import { FIELD, type MatchState, type Player } from "@golazo/shared";
import { lerp } from "../core/math.js";
import { replayProgress, varStage } from "../game/replay.js";

/**
 * A quien mira la camara en cada momento.
 *
 * La repeticion del gol se ve desde el que lo metio, no desde el jugador que
 * llevas. Con la camara de siempre, un gol del rival —o uno tuyo marcado lejos
 * de donde estabas— se repetia enseñando el sitio equivocado: "aqui estabas tu
 * mientras alguien marcaba" en vez de "aqui esta el gol".
 *
 * Ojo al limite: la CELEBRACION (zoom y cartel) sigue siendo del jugador. Solo
 * cambia el plano cuando empieza el clip, que es cuando el tiempo vuelve atras
 * y el encuadre deja de tener que ver con lo que esta pasando ahora.
 */
export function cameraTargetId(
  state: MatchState,
  humanId: string,
  scorerId: string | null,
): string {
  /*
   * En la revision de VAR el plano es del SANCIONADO, no del humano.
   *
   * Y vale para toda la fase, no solo para el clip: en el congelado es cuando
   * se traza la linea, y encuadrar al jugador equivocado justo ahi convertiria
   * la prueba en un plano de otra parte del campo.
   */
  if (state.phase === "offside" && state.offsideReview) return state.offsideReview.playerId;
  return replayProgress(state) > 0 && scorerId ? scorerId : humanId;
}

/**
 * A donde mira la camara en el congelado del VAR.
 *
 * Tiene que ser un plano PROPIO, y no el de la repeticion. La repeticion va al
 * balon —es lo correcto mientras se ve la jugada— pero en el congelado el balon
 * esta en el pie del pasador, a media cancha de donde ocurre la infraccion: se
 * encuadraba a quien dio el pase y la linea se quedaba fuera del plano, que es
 * lo unico que la revision existe para enseñar.
 *
 * El punto es el centro entre las dos lineas, empujado hacia el atacante: es el
 * hueco lo que hay que ver, y el que se ha pasado tiene que caber en el.
 * `null` fuera del congelado.
 */
export function offsideFocus(state: MatchState): { x: number; y: number } | null {
  if (varStage(state) !== "freeze") return null;
  const review = state.offsideReview;
  if (!review) return null;
  return {
    x: review.defender.x + (review.attacker.x - review.defender.x) * 0.65,
    y: (review.defender.y + review.attacker.y) / 2,
  };
}

/**
 * Acercamiento extra cuando el balon esta DENTRO de la porteria.
 *
 * La repeticion sigue al balon, y cuando el balon entra es cuando pasa lo que
 * se quiere ver: la red hundiendose. Sin esto la camara se queda al mismo
 * plano que el resto de la jugada y el detalle por el que existe toda la
 * fisica del paño no se aprecia.
 *
 * Entra progresivo con la profundidad del balon en la red, no de golpe: un
 * salto de zoom en mitad de una repeticion se lee como un fallo.
 */
export function netZoom(ballX: number, inReplay: boolean): number {
  if (!inReplay) return 1;
  const past =
    ballX < 0 ? -ballX : ballX > FIELD.WIDTH ? ballX - FIELD.WIDTH : 0;
  if (past <= 0) return 1;
  const depth = Math.min(1, past / FIELD.GOAL_DEPTH);
  return 1 + depth * 0.34;
}

export interface FollowCamera {
  /** Punto de campo (0..FIELD.WIDTH) que la camara quiere tener en el centro. */
  readonly x: number;
  readonly y: number;
  /**
   * Avanza el encuadre. `dt` es tiempo de RENDER, no de simulacion, y `alpha`
   * es la misma interpolacion con la que se PINTA al jugador.
   */
  update(dt: number, controlled: Player | null, alpha: number): void;
  /** Corta cualquier transicion: saques, goles, cambios de modo. */
  snap(x?: number, y?: number): void;
}

/**
 * La camara va CLAVADA al jugador que llevas.
 *
 * La primera version seguia al balon, con adelanto por velocidad y un poco de
 * peso del jugador. Se descarto al jugarlo: el balon cambia de direccion cada
 * pase y cada rebote, asi que la camara era un vaiven constante — y encima el
 * jugador, que es lo unico que controlas, no paraba de moverse por la pantalla.
 *
 * Ahora el jugador esta en el centro exacto y no se mueve nunca de ahi (salvo
 * en los bordes del campo, donde manda el recorte del encuadre). Lo que se
 * mueve es el mundo. Y el balon se sigue por el radar, que para eso esta.
 *
 * Por eso tampoco hay suavizado en el movimiento normal: cualquier retardo
 * significa que el jugador se despega del centro justo cuando aceleras, que es
 * exactamente lo que se queria quitar.
 *
 * Y por eso se usa la posicion INTERPOLADA, no `player.pos` a secas: la
 * simulacion va a 60 Hz y el dibujo a los Hz de la pantalla, asi que apuntar a
 * la posicion cruda dejaria al jugador vibrando alrededor del centro — un tick
 * de movimiento de amplitud — mientras su ficha se pinta suave.
 */

/**
 * Segundos de panoramica al cambiar de jugador con Tab/Q. Es el UNICO caso con
 * transicion: cortar de golpe a otro punto del campo desorienta, y aqui el
 * retardo no molesta porque el jugador nuevo aun no lo estas pilotando.
 */
const SWITCH_PAN = 0.22;

export function createFollowCamera(): FollowCamera {
  let x = FIELD.WIDTH / 2;
  let y = FIELD.HEIGHT / 2;
  let watchedId: string | null = null;
  let panLeft = 0;

  return {
    get x(): number {
      return x;
    },
    get y(): number {
      return y;
    },

    update(dt: number, controlled: Player | null, alpha: number): void {
      if (!controlled) return;

      if (controlled.id !== watchedId) {
        // Cambio de jugador: se abre una panoramica corta hacia el nuevo.
        if (watchedId !== null) panLeft = SWITCH_PAN;
        watchedId = controlled.id;
      }

      const targetX = lerp(controlled.prevPos.x, controlled.pos.x, alpha);
      const targetY = lerp(controlled.prevPos.y, controlled.pos.y, alpha);

      if (panLeft <= 0 || dt <= 0) {
        x = targetX;
        y = targetY;
        panLeft = 0;
        return;
      }

      // Interpolacion que TERMINA: al agotarse la panoramica el jugador queda
      // en el centro exacto, no acercandose a el para siempre.
      const step = Math.min(1, dt / panLeft);
      x += (targetX - x) * step;
      y += (targetY - y) * step;
      panLeft = Math.max(0, panLeft - dt);
    },

    snap(nextX = FIELD.WIDTH / 2, nextY = FIELD.HEIGHT / 2): void {
      x = nextX;
      y = nextY;
      panLeft = 0;
    },
  };
}
